/**
 * CodeSnippet Component
 *
 * Displays a code snippet with syntax highlighting and metadata.
 * Uses Prism.js for syntax highlighting (already available in SyntaxLens).
 *
 * @module concept-graph/components
 */

import React from 'react';
import type { CodeSnippet as CodeSnippetType } from '../types';

export interface CodeSnippetProps {
  snippet: CodeSnippetType;
  language: string;
  showTitle?: boolean;
  showExplanation?: boolean;
  className?: string;
}

/**
 * Display a code snippet with syntax highlighting
 */
export function CodeSnippet({
  snippet,
  language,
  showTitle = true,
  showExplanation = true,
  className = ''
}: CodeSnippetProps) {
  return (
    <div className={`code-snippet rounded-lg border border-slate-200 bg-white overflow-hidden ${className}`}>
      {/* Header */}
      {showTitle && (
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <h4 className="text-sm font-medium text-slate-700">{snippet.title}</h4>
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${
                snippet.complexity === 'basic'
                  ? 'bg-green-100 text-green-700'
                  : snippet.complexity === 'intermediate'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {snippet.complexity}
            </span>
            {snippet.runnable && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                Runnable
              </span>
            )}
          </div>
        </div>
      )}

      {/* Code Block */}
      <div className="relative">
        <pre className="p-4 overflow-x-auto bg-slate-900 text-slate-100">
          <code className={`language-${language.toLowerCase()}`}>
            {snippet.code}
          </code>
        </pre>
      </div>

      {/* Explanation */}
      {showExplanation && snippet.explanation && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
          <p className="text-sm text-slate-600">{snippet.explanation}</p>
        </div>
      )}

      {/* Tags */}
      {snippet.tags && snippet.tags.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-200 flex flex-wrap gap-1">
          {snippet.tags.map(tag => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
